import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import { cn } from "@/lib/utils";

const SIX_HOURS_MS = 6 * 60 * 60 * 1000;

/** `"12m ago"`, `"3h ago"`, `"2d ago"` - coarser than a stopwatch on purpose. */
function ago(at: string, now: number): string {
  const minutes = Math.max(0, Math.round((now - Date.parse(at)) / 60_000));
  if (minutes < 60) return `${String(minutes)} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${String(hours)} h ago`;
  return `${String(Math.round(hours / 24))} d ago`;
}

/** The newest successful sweep across every resource. */
function lastSweep(
  resources: readonly { lastSuccessAt: string | null }[],
): string | null {
  const finished = resources
    .map((resource) => resource.lastSuccessAt)
    .filter((at): at is string => at !== null);
  return finished.length === 0
    ? null
    : finished.reduce((latest, at) => (at > latest ? at : latest));
}

/**
 * Sync health as one line at the foot of the dashboard (#528), where it used
 * to be a tile. Healthy while a webhook arrived in the last six hours; the
 * state is said in words, not only by the dot.
 *
 * Absent for someone who cannot read sync status: the line is a convenience,
 * and a refusal here is not an error worth a message on the dashboard.
 */
export function SyncFooter({ now }: { now: number }) {
  const data = useDataSource();
  const permissions = usePermissions();
  const query = useQuery({
    queryKey: ["dashboard", "sync-status"],
    queryFn: () => data.settings.getSyncStatus(),
    enabled: can(permissions, "settings.sync.view"),
  });

  if (!query.data) return null;
  const sweep = lastSweep(query.data.resources);
  const webhook = query.data.webhooks.mostRecentAt;
  const stale = webhook === null || now - Date.parse(webhook) > SIX_HOURS_MS;

  return (
    <div className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 border-t pt-3 text-xs">
      <span className="inline-flex items-center gap-1.5">
        <span
          aria-hidden
          className={cn(
            "size-2 rounded-full",
            stale ? "bg-warning" : "bg-tint-green-foreground",
          )}
        />
        <span className={cn(stale && "text-foreground font-medium")}>
          {stale ? "Shopify sync looks stale" : "Shopify sync healthy"}
        </span>
      </span>
      <span>Last sweep {sweep ? ago(sweep, now) : "never"}</span>
      <span>Last webhook {webhook ? ago(webhook, now) : "never"}</span>
      <Link
        to="/settings/sync"
        className="text-primary-strong ml-auto inline-flex min-h-6 items-center font-medium underline-offset-2 hover:underline pointer-coarse:min-h-11"
      >
        Sync details
      </Link>
    </div>
  );
}
